import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/** ⚠ The shared taxonomy — the same four categories in every app. */
export const CATEGORIES = ["Bug", "Idea", "Question", "Other"] as const;
export type Category = (typeof CATEGORIES)[number];

/** One line each, so people pick the right one without guessing. */
export const CATEGORY_HINT: Record<Category, string> = {
  Bug: "something is broken or wrong",
  Idea: "a suggestion or improvement",
  Question: "not sure how something works",
  Other: "anything else",
};

export const BODY_MAX = 4000;

export const STATUSES = ["New", "In Progress", "Resolved", "Declined"] as const;
export type Status = (typeof STATUSES)[number];

export interface FeedbackItem {
  id: string;
  category: Category;
  body: string;
  page: string | null;
  status: Status;
  adminRemark: string | null;
  raiserUnseen: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ManagedFeedback extends FeedbackItem {
  raiserEmail: string;
  raiserName: string | null;
}

const MINE = ["feedback", "mine"] as const;
const ALL = ["feedback", "all"] as const;

export function useMyFeedback() {
  return useQuery({
    queryKey: MINE,
    queryFn: () => api.get<{ feedback: FeedbackItem[]; unseen: number }>("/feedback/mine"),
  });
}

export function useSubmitFeedback() {
  const qc = useQueryClient();
  return useMutation({
    // The page is context for whoever triages it — captured here, never typed.
    mutationFn: (v: { category: Category; body: string }) =>
      api.post("/feedback", {
        ...v,
        page: `${window.location.pathname}${window.location.search}`,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MINE }),
  });
}

export function useMarkSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/feedback/${id}/seen`),
    onSuccess: () => qc.invalidateQueries({ queryKey: MINE }),
  });
}

export function useAllFeedback(status: Status | "All", category: Category | "All") {
  return useQuery({
    queryKey: [...ALL, status, category],
    queryFn: () =>
      api.get<{ feedback: ManagedFeedback[] }>(
        `/feedback/all?status=${encodeURIComponent(status)}&category=${encodeURIComponent(category)}`,
      ),
    select: (d) => d.feedback,
  });
}

export function useUpdateFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status?: Status; adminRemark?: string | null }) =>
      api.patch(`/feedback/${v.id}`, { status: v.status, adminRemark: v.adminRemark }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ALL });
      void qc.invalidateQueries({ queryKey: MINE });
    },
  });
}
